import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Container, Card, CardContent, Typography, Button, CircularProgress
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  MarkEmailRead as EmailIcon
} from '@mui/icons-material';
import axios from 'axios';

function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [status, setStatus] = useState(token ? 'ready' : 'error');
  const [message, setMessage] = useState(token ? '' : 'No verification token provided. Please check your email link.');

  const handleVerify = async () => {
    if (!token || status === 'verifying') return;
    setStatus('verifying');
    setMessage('');
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
      const response = await axios.post(`${apiBaseUrl}/api/v1/ypaas/auth/verify-email`, { token });
      setStatus('success');
      setMessage(response.data.message || 'Email verified successfully!');
      setTimeout(() => navigate('/login'), 3000);
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.error || 'Verification failed. The link may be invalid or expired.');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="sm">
        <Card elevation={3}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            {status === 'ready' && (
              <>
                <EmailIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
                <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                  Confirm Your Email
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.8 }}>
                  Click the button below to verify your email address and activate your MVYP account.
                </Typography>
                <Button variant="contained" size="large" onClick={handleVerify} sx={{ px: 4 }}>
                  Verify My Email
                </Button>
              </>
            )}

            {status === 'verifying' && (
              <>
                <CircularProgress size={80} sx={{ color: 'primary.main', mb: 3 }} />
                <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                  Verifying Your Email
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Please wait while we verify your email address...
                </Typography>
              </>
            )}

            {status === 'success' && (
              <>
                <SuccessIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, mb: 2, color: 'success.main' }}>
                  Email Verified!
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>{message}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>Redirecting to login page...</Typography>
                <Button component={RouterLink} to="/login" variant="contained" color="success" size="large" sx={{ px: 4 }}>
                  Go to Login Now
                </Button>
              </>
            )}

            {status === 'error' && (
              <>
                <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
                <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, mb: 2, color: 'error.main' }}>
                  Verification Failed
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>{message}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                  Please try creating a new account or contact support if you need assistance.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button component={RouterLink} to="/login" variant="outlined" size="large" sx={{ px: 4 }}>Go to Login</Button>
                  <Button component={RouterLink} to="/create-account" variant="contained" size="large" sx={{ px: 4 }}>Create Account</Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}

export default VerifyEmail;
